import { QueryClient } from "react-query";
import { clearLaunchCreds } from "./launchCreds";

/**
 * Handles calls to the api server.
 * Auth is an httpOnly cookie the browser attaches automatically (credentials: "include") —
 * there's no session id in JS to add to requests.
 */
export default class ApiService {
  static apiUrl = process.env.REACT_APP_API_URL;
  static queryClient = new QueryClient({
    // defaultOptions: { queries: { staleTime: 5000 } },
  });

  /**
   * Make a request to the api server
   * @param {string} method HTTP method
   * @param {string} path HTTP path
   * @param {object} body HTTP body
   * @returns Response data
   */
  public static async fetch<T, B = unknown>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    path: string,
    body?: B
  ): Promise<T> {
    const request: RequestInit = {
      body: method !== "GET" ? JSON.stringify(body) : undefined,
      method,
      headers: this.getHeaders(),
      credentials: "include",
    };

    const response = await fetch(this.apiUrl + path, request);

    return this.handleResponse(response);
  }

  /**
   * Ask the backend "am I logged in" — the session cookie is httpOnly, so this is the only
   * way the frontend can know. Returns undefined if there's no valid session, or if the
   * backend can't be reached at all (e.g. the unauthenticated smoke test runs the frontend
   * with no backend up — AppRouter falls back to the login screen either way, matching the
   * old localStorage check, which never needed a backend to say "not logged in" either).
   */
  public static async getSession(): Promise<SessionInfo | undefined> {
    try {
      const response = await this.fetch<{ data: SessionInfo | null }>(
        "GET",
        "/api/session"
      );
      return response.data ?? undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Synchronous read of the last session the ["session"] query resolved to. Safe to call from
   * anywhere already rendering inside AppRouter's authenticated route tree — that tree only
   * mounts once the initial ["session"] query has resolved, so the cache is already populated.
   */
  public static getCachedSession(): SessionInfo | undefined {
    return this.queryClient.getQueryData<SessionInfo>(["session"]);
  }

  private static getHeaders() {
    return {
      "Content-Type": "application/json",
      // Belt-and-suspenders CSRF guard the backend requires on mutating routes (see
      // requireCsrfHeader) — trivial for same-origin fetch to set, impossible for a forged
      // cross-site form/image request to include.
      "X-Requested-With": "XMLHttpRequest",
    };
  }

  private static async handleResponse(response: Response) {
    const data = await response.json();

    if (response.ok) {
      return data;
    } else if (response.status === 401) {
      // Session is no longer valid — clear both the manager session and any saved
      // launch creds so they don't persist past the end of this auth session.
      clearLaunchCreds();
      this.queryClient.invalidateQueries(["session"]);
    }

    const error = new Error(data.error?.message);
    error.name = data.error?.name || "Error";
    throw error;
  }
}
